import { ApplicantPortalOption } from '$constants/applicantPortalOption';

import { SEARCH_PARAMS_KEYS } from '$constants';
import { LANGUAGES } from '$locale/constants';

/**
 * The `dfgCode` garuda's root-creation gate returns with its 403 when the option
 * owning the request is hidden. Non-retryable by design — a hidden option never
 * becomes available by retrying, so no caller may re-issue the request.
 */
export const OPTION_NOT_AVAILABLE_ERROR_CODE = 'OPTION_NOT_AVAILABLE';

export enum RouteMatchMode {
  /** Pathname equals the rule path. Used where children must stay reachable. */
  EXACT = 'EXACT',
  PREFIX = 'PREFIX'
}

export type OptionRouteRule = {
  readonly path: string;
  readonly mode: RouteMatchMode;
  readonly option: ApplicantPortalOption;
  /**
   * When true, the rule is skipped if the query string names an existing
   * verification request — i.e. gate self-initiation only, never an invited
   * applicant resuming a submission the client created. Set on the two routes
   * that serve both purposes.
   */
  readonly exemptWhenResumingExistingRequest?: boolean;
  /**
   * When true, the rule is skipped if `redirectAfterFetchReport` sends the
   * applicant back to an invited-funnel page that names an existing
   * verification request. Set on Find Report: an invited applicant can use it
   * from inside their submission, but direct URL access stays gated.
   */
  readonly exemptWhenReturningToExistingRequest?: boolean;
};

/**
 * The canonical list of gated URLs. `/dashboard/verification` is `EXACT`
 * because `/dashboard/verification/verification-details` must stay reachable —
 * every invited resume card and every `TRACK_STATUS` card deep-links into it.
 *
 * `/verification/pqrSelection` is the one rule an invited applicant can
 * legitimately hit, because the `INVITED_BY_*_RESUME` cards point at it. Its
 * `exemptWhenResumingExistingRequest` is what makes that scenario possible.
 *
 * `/verification/find-report` is gated so the Check Status entry point and
 * direct URL access are blocked (DT-57425). Its sub-pages carry the origin page
 * only inside `redirectAfterFetchReport`, so it uses
 * `exemptWhenReturningToExistingRequest` instead.
 */
export const OPTION_ROUTE_RULES: ReadonlyArray<OptionRouteRule> = [
  {
    // Both of the gateway's exits land in the self-initiation funnel, so the
    // entry screen is gated by the same option rather than left reachable by
    // direct URL under a `START_NEW_VERIFICATION` restriction.
    path: '/verification/applicant-gateway',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.START_NEW_VERIFICATION
  },
  {
    path: '/verification/select-country',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.START_NEW_VERIFICATION
  },
  {
    path: '/verification/select-authority',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.START_NEW_VERIFICATION
  },
  {
    path: '/verification/add-speciality',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.START_NEW_VERIFICATION
  },
  {
    path: '/verification/pqrSelection',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.START_NEW_VERIFICATION,
    exemptWhenResumingExistingRequest: true
  },
  {
    path: '/verification/browseExistingCases',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.START_NEW_VERIFICATION,
    exemptWhenResumingExistingRequest: true
  },
  {
    path: '/verification/find-report',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.START_NEW_VERIFICATION,
    exemptWhenReturningToExistingRequest: true
  },
  {
    path: '/verification/ancillary-interstitial',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.ANCILLARY_SERVICES
  },
  {
    path: '/dashboard/services',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.ANCILLARY_SERVICES
  },
  {
    path: '/services/',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.ANCILLARY_SERVICES
  },
  {
    path: '/dashboard/verification',
    mode: RouteMatchMode.EXACT,
    option: ApplicantPortalOption.CHECK_VERIFICATION_STATUS
  },
  {
    path: '/dashboard/vault',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.TRUEPROFILE_WALLET
  },
  {
    path: '/dashboard/manage-account',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.SETTINGS
  },
  {
    path: '/dashboard/account-option',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.SETTINGS
  },
  {
    path: '/dashboard/user-profile',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.MANAGE_PROFILE
  },
  {
    path: '/dashboard/transactions',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.VIEW_TRANSACTIONS
  },
  {
    path: '/dashboard/home/notifications',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.NOTIFICATIONS
  },
  {
    path: '/dashboard/notification-category',
    mode: RouteMatchMode.PREFIX,
    option: ApplicantPortalOption.NOTIFICATIONS
  }
];

/**
 * Routes that must never be gated. **Not consulted at runtime** — a pathname no
 * rule matches is ungated by construction. This is documentation plus the
 * subject of unit-test assertions (disjointness from the rule paths, and that
 * every entry resolves to `null`).
 */
export const NEVER_GATED_PREFIXES: ReadonlyArray<string> = [
  // Client's own portal. `/dashboard/home` stays reachable because the
  // `/dashboard/home/notifications` rule is a longer path and wins the
  // longest-match scan.
  '/onboarding',
  '/introduction',
  '/dashboard/home',
  '/dashboard/verification/verification-details',

  // The whole shared funnel.
  '/verification/required-documents',
  '/verification/select-documents',
  '/verification/select-issuers',
  '/verification/document-upload',
  '/verification/client-requirement-list-tab',
  '/verification/letter-of-auth',
  '/verification/overview',
  '/verification/overview-summary',
  '/verification/submit-application',
  '/verification/pricing-details',
  '/verification/pricing-estimate',
  '/verification/insufficiency-pricing-details',
  '/verification/verification-insufficiency',
  '/verification/report-transfer',
  '/verification/additional-components',
  '/verification/name-change',
  '/verification/appeal-request',
  '/verification/appeal-pricing',
  '/verification/request-correction',
  '/verification/fetch-report-pricing',
  '/verification/reissue-report',
  '/verification/check-migrate',
  '/verification/ia-attribution',

  // Terminal callbacks — a payment gateway or UAE Pass redirects into these,
  // and blocking one strands a paid applicant.
  '/verification/payment-status',
  '/verification/vr-success',
  '/verification/additional-authentication',

  // Unauthenticated utility.
  '/validate-trueproof'
];

/**
 * True when the query string names an existing verification request.
 *
 * Deliberately looser than `pqrSelection`'s own logic, which also requires
 * `partnerInvite === 'true'` (case-sensitively). Looser errs toward letting an
 * invited applicant through, which is the safe direction: a forged
 * `verificationRequestId` only un-gates a route whose funnel still terminates
 * at the creation gate.
 */
const namesExistingVerificationRequest = (
  searchParams: URLSearchParams
): boolean =>
  Boolean(searchParams.get(SEARCH_PARAMS_KEYS.verificationRequestId));

/**
 * Base that lets `new URL` parse a relative URL such as `/en/verification/…`.
 * `.invalid` is a reserved TLD (RFC 2606), so no request ever goes to it. A
 * relative URL takes this origin, so any other origin means the input was an
 * absolute URL.
 */
const URL_PARSE_BASE = 'http://placeholder.invalid';
const URL_PARSE_BASE_ORIGIN = new URL(URL_PARSE_BASE).origin;

/**
 * Invited-funnel pages that open Find Report and receive the applicant back.
 * Each one carries the `verificationRequestId` of the submission in progress.
 */
const INVITED_FIND_REPORT_RETURN_PATHS: ReadonlyArray<string> = [
  '/verification/report-transfer',
  '/verification/pqrSelection',
  '/verification/browseExistingCases'
];

/**
 * True when `redirectAfterFetchReport` is a relative URL to an invited-funnel
 * page that names an existing verification request.
 *
 * A forged value un-gates only Find Report itself. Find Report creates no
 * verification request: it searches for reports and transfers their ownership
 * to the signed-in applicant. It then returns to a page that is exempt or
 * never gated, so nothing reaches the creation gate.
 */
const returnsToExistingVerificationRequest = (
  searchParams: URLSearchParams
): boolean => {
  const redirect = searchParams.get(
    SEARCH_PARAMS_KEYS.redirectAfterFetchReport
  );
  if (!redirect) return false;

  try {
    const target = new URL(redirect, URL_PARSE_BASE);
    // Only a relative redirect counts. The entry points always build one.
    if (target.origin !== URL_PARSE_BASE_ORIGIN) return false;

    const targetPath = stripLanguageSegment(target.pathname);
    const isInvitedReturnPage = INVITED_FIND_REPORT_RETURN_PATHS.some(
      (path) => targetPath === path || targetPath.startsWith(`${path}/`)
    );

    return (
      isInvitedReturnPage &&
      namesExistingVerificationRequest(target.searchParams)
    );
  } catch {
    return false;
  }
};

/** True when an invited applicant must pass the rule. */
const isExemptForExistingRequest = (
  rule: OptionRouteRule,
  searchParams: URLSearchParams
): boolean =>
  (Boolean(rule.exemptWhenResumingExistingRequest) &&
    namesExistingVerificationRequest(searchParams)) ||
  (Boolean(rule.exemptWhenReturningToExistingRequest) &&
    returnsToExistingVerificationRequest(searchParams));

/** Longest path first, so `/dashboard/home/notifications` is tested before `/dashboard/home`. */
const SORTED_RULES: ReadonlyArray<OptionRouteRule> = [
  ...OPTION_ROUTE_RULES
].sort((a, b) => b.path.length - a.path.length);

/**
 * The option that owns `pathname`, or null when no rule does.
 * `pathname` must already have its `/{lang}` prefix stripped.
 */
export const resolveOptionForRoute = (
  pathname: string,
  searchParams: URLSearchParams
): ApplicantPortalOption | null => {
  const rule = SORTED_RULES.find((candidate) =>
    candidate.mode === RouteMatchMode.EXACT
      ? pathname === candidate.path
      : pathname.startsWith(candidate.path)
  );

  if (!rule) return null;

  // The scan STOPS here rather than falling through to a shorter prefix, so an
  // invited pqrSelection or Find Report is ungated rather than re-matched.
  if (isExemptForExistingRequest(rule, searchParams)) {
    return null;
  }

  return rule.option;
};

/** `/en/dashboard/vault` → `/dashboard/vault`. The layout belt's `path.replace(…)` is equivalent. */
export const stripLanguageSegment = (pathname: string): string => {
  const lang = LANGUAGES.find(
    (l) => pathname === `/${l}` || pathname.startsWith(`/${l}/`)
  );
  return lang ? pathname.slice(`/${lang}`.length) || '/' : pathname;
};

/**
 * The option that owns a config-driven URL, or null when no rule does.
 *
 * Applicant banners carry targets an operator edits in a KV pair, so any one of
 * them may point into a gated funnel. They are resolved by path through the
 * canonical `OPTION_ROUTE_RULES` rather than given their own id→option map, so a
 * banner stays correctly gated as the config changes.
 *
 * A cross-host target is matched on its path deliberately: the banner is
 * rendered *inside* the restricted portal even when what it links to lives
 * elsewhere, so `add-speciality` on another host must still disappear under a
 * `START_NEW_VERIFICATION` restriction. Malformed values resolve to null rather
 * than throwing, so a bad config entry cannot break a render.
 */
export const resolveOptionForUrl = (
  url: string | undefined
): ApplicantPortalOption | null => {
  if (!url) return null;

  try {
    const parsed = new URL(url, URL_PARSE_BASE);
    return resolveOptionForRoute(
      stripLanguageSegment(parsed.pathname),
      // The parsed query is what keeps `exemptWhenResumingExistingRequest`
      // behaving the same here as it does in the route guard.
      parsed.searchParams
    );
  } catch {
    return null;
  }
};
